import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import adsApi from './adsApi';

export default function AdsSettingsPage() {
  const [form, setForm] = useState({
    adsense_enabled: false,
    adsense_publisher_id: '',
    adsense_slot_map: '{}',
    tracking_enabled: true,
    default_currency: 'INR',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const res = await adsApi.getSettings();
        const s = res?.data || {};
        setForm({
          adsense_enabled: !!s.adsense_enabled,
          adsense_publisher_id: s.adsense_publisher_id || '',
          adsense_slot_map: JSON.stringify(s.adsense_slot_map || {}, null, 2),
          tracking_enabled: s.tracking_enabled !== false,
          default_currency: s.default_currency || 'INR',
        });
      } catch (err) {
        setError(err.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await adsApi.updateSettings({
        adsense_enabled: form.adsense_enabled,
        adsense_publisher_id: form.adsense_publisher_id || null,
        adsense_slot_map: JSON.parse(form.adsense_slot_map || '{}'),
        tracking_enabled: form.tracking_enabled,
        default_currency: form.default_currency,
      });
      setMessage('Settings saved');
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner size="large" message="Loading ad settings..." />;

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 md:p-6">
      <h1 className="text-2xl font-bold text-slate-900">Ads Settings</h1>
      <p className="text-sm text-slate-600">
        Configure Google AdSense fallback when no direct or sponsored campaign fills a placement.
      </p>
      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}
      {message ? (
        <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{message}</div>
      ) : null}
      <form onSubmit={onSave} className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.adsense_enabled}
            onChange={(e) => setForm((f) => ({ ...f, adsense_enabled: e.target.checked }))}
          />
          Enable Google AdSense fallback
        </label>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Publisher ID</label>
          <input
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="ca-pub-xxxxxxxx"
            value={form.adsense_publisher_id}
            onChange={(e) => setForm((f) => ({ ...f, adsense_publisher_id: e.target.value }))}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">
            Global slot map JSON (placement code → slot id)
          </label>
          <textarea
            className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-xs"
            rows={8}
            value={form.adsense_slot_map}
            onChange={(e) => setForm((f) => ({ ...f, adsense_slot_map: e.target.value }))}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.tracking_enabled}
            onChange={(e) => setForm((f) => ({ ...f, tracking_enabled: e.target.checked }))}
          />
          Enable impression / click tracking
        </label>
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </form>
    </div>
  );
}
